/**
 * Semantic CTA detection.
 *
 * The previous checklist only matched literal phrases like "call to action"
 * or "learn more". Real blog posts almost never use those words. This
 * detector looks at the END of the post (last 2–3 paragraphs) for
 * signals that together indicate the writer wrapped with a call to
 * action, even if they never said "call to action":
 *
 *   1. Imperative action verb in the closing paragraphs (try, start, grab,
 *      book, download, join, schedule, subscribe, discover, etc.)
 *   2. A link (<a href>) near the end — strong signal of "click here"
 *   3. A question directed at the reader in the closing paragraph
 *   4. Second-person pronouns ("you", "your") + action verb in the same
 *      sentence near the end
 *   5. A trailing `<strong>` emphasis block
 *
 * A post scores 'present' if it collects 2+ signals, 'weak' if it collects
 * exactly 1, and 'absent' otherwise. The checklist treats 'present' and
 * 'weak' as passing for the CTA item, but the brand-score modal shows
 * the full breakdown so the user can see why.
 */

const ACTION_VERBS = [
    'try', 'start', 'grab', 'book', 'download', 'join', 'schedule',
    'subscribe', 'discover', 'explore', 'read', 'learn', 'get', 'find',
    'visit', 'contact', 'reach', 'sign up', 'claim', 'reserve', 'apply',
    'request', 'register', 'watch', 'listen', 'follow', 'share', 'tell',
    'see', 'check out', 'give', 'take', 'tap', 'click', 'buy', 'shop',
    'order', 'pick', 'build', 'create', 'unlock', 'save', 'meet',
    'upgrade', 'switch', 'make', 'let', 'dive', 'step',
];

function stripHtml(html) {
    if (!html) return '';
    return String(html)
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim();
}

// Extract the trailing N paragraphs from HTML. Falls back to the last
// N sentences if there are no <p> tags.
function getTail(html, n = 2) {
    if (!html) return '';
    const paragraphs = Array.from(html.matchAll(/<p[\s>][\s\S]*?<\/p\s*>/gi)).map((m) => m[0]);
    if (paragraphs.length >= n) return paragraphs.slice(-n).join(' ');
    if (paragraphs.length > 0) return paragraphs.join(' ');
    // No <p> tags — fall back to sentence split on the plain text
    const text = stripHtml(html);
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
    return sentences.slice(-n).join(' ');
}

/**
 * Detect whether a post has a call to action near the end.
 *
 * @param {string} html - the full post body HTML
 * @returns {{
 *   present: boolean,
 *   strength: 'strong'|'weak'|'absent',
 *   signals: string[],
 *   score: number  // 0..5
 * }}
 */
export function detectCta(html) {
    const result = { present: false, strength: 'absent', signals: [], score: 0 };
    if (!html) return result;

    const tailHtml = getTail(html, 3);
    const tailText = stripHtml(tailHtml).toLowerCase();

    // Signal 1: imperative action verb
    const verbPattern = new RegExp('\\b(' + ACTION_VERBS.join('|') + ')\\b', 'i');
    if (verbPattern.test(tailText)) {
        result.signals.push('Action verb near the end');
        result.score += 1;
    }

    // Signal 2: a link in the tail
    if (/<a\s[^>]*href/i.test(tailHtml)) {
        result.signals.push('Link in closing paragraph');
        result.score += 2; // links are a strong signal
    }

    // Signal 3: reader-directed question
    if (/\?\s*$/.test(tailText.trim()) || /\?\s*<\/p/i.test(tailHtml)) {
        result.signals.push('Direct question to reader');
        result.score += 1;
    }

    // Signal 4: second-person pronoun + verb in the same sentence
    const youVerbPattern = new RegExp('\\byou(?:r|\'ll|\'re| can| will)?\\b[^.!?]{0,80}\\b(' + ACTION_VERBS.join('|') + ')\\b', 'i');
    if (youVerbPattern.test(tailText)) {
        result.signals.push('Direct "you" + action verb');
        result.score += 1;
    }

    // Signal 5: trailing <strong> or <b> emphasis
    if (/<(strong|b)[\s>]/i.test(tailHtml)) {
        result.signals.push('Emphasis (<strong>) in closing');
        result.score += 1;
    }

    // Signal 6: literal CTA phrases (legacy fallback — still counts)
    const literal = /\b(call to action|sign up|get started|learn more|try it|try our|free trial|book a|schedule a|request a|contact us|join us|subscribe)\b/i;
    if (literal.test(tailText)) {
        result.signals.push('Classic CTA phrase');
        result.score += 2;
    }

    if (result.score >= 3) {
        result.present = true;
        result.strength = 'strong';
    } else if (result.score >= 1) {
        result.present = true;
        result.strength = 'weak';
    }
    return result;
}

/**
 * Boolean shortcut for the checklist.
 */
export function hasCta(html) {
    return detectCta(html).present;
}
